import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';
import LoginHelp from '../components/LoginHelp';
import { getLoginConfig, hasOAuthMethods } from '../config/loginConfig';

/**
 * Login and registration page with Google OAuth integration
 */
const Login: React.FC = () => {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get login configuration from environment variables
  const loginConfig = getLoginConfig();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Check for OAuth redirect path in sessionStorage
      const oauthRedirect = sessionStorage.getItem('oauth_redirect');
      if (oauthRedirect) {
        sessionStorage.removeItem('oauth_redirect');
        navigate(oauthRedirect, { replace: true });
        return;
      }

      // Otherwise use the from location or default to upcoming matches
      const from = (location.state as any)?.from?.pathname || '/upcoming-matches';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Handle URL error parameters (token is handled by AuthContext)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const errorParam = urlParams.get('error');

    if (errorParam) {
      const detail = urlParams.get('detail');
      let errorMsg: string;

      switch (errorParam) {
        case 'google_auth_failed':
          errorMsg = t('errors:auth.googleAuthFailed');
          break;
        case 'microsoft_auth_failed':
          errorMsg = t('errors:auth.microsoftAuthFailed');
          break;
        case 'no_email':
          errorMsg = t('errors:auth.noEmailProvided');
          break;
        case 'auth_processing_failed':
          errorMsg = t('errors:auth.authProcessingFailed');
          break;
        case 'callback_error':
          errorMsg = t('errors:auth.callbackError');
          break;
        default:
          errorMsg = t('errors:auth.loginFailed');
      }

      // Append detail for diagnostics (visible to user + console)
      if (detail) {
        console.error(`AUTH_ERROR: ${errorParam} – ${detail}`);
        errorMsg += ` (${detail})`;
      }

      setError(errorMsg);

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, displayName || undefined);
      }
      
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || t('errors:auth.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    authApi.googleLogin();
  };

  const handleMicrosoftLogin = () => {
    authApi.microsoftLogin();
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setEmail('');
    setPassword('');
    setDisplayName('');
  };

  return (
    <div className="page-container" style={containerStyle}>
      <div style={formContainerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>
            {isLogin ? t('auth:login.title') : t('auth:register.title')}
          </h1>
          <p style={subtitleStyle}>
            {isLogin
              ? t('auth:login.subtitle')
              : t('auth:register.subtitle')
            }
          </p>
        </div>

        {error && (
          <div style={errorStyle}>
            {error}
          </div>
        )}

        {/* Email/Password Login Form */}
        {loginConfig.enableEmailLogin && (
          <>
            <form onSubmit={handleSubmit} style={formStyle}>
              <div style={fieldGroupStyle}>
                <label htmlFor="email" style={labelStyle}>{t('auth:login.email')}</label>
                <input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={inputStyle}
                  placeholder={t('auth:login.email')}
                  disabled={isLoading}
                />
              </div>

              {!isLogin && (
                <div style={fieldGroupStyle}>
                  <label htmlFor="displayName" style={labelStyle}>{t('auth:register.username')}</label>
                  <input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    style={inputStyle}
                    placeholder={t('auth:register.username')}
                    disabled={isLoading}
                  />
                </div>
              )}

              <div style={fieldGroupStyle}>
                <label htmlFor="password" style={labelStyle}>{t('auth:login.password')}</label>
                <input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={inputStyle}
                  placeholder={t('auth:login.password')}
                  disabled={isLoading}
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                data-testid="submit-button"
                style={submitButtonStyle}
                disabled={isLoading}
              >
                {isLoading ? t('common:loading') : (isLogin ? t('auth:login.submit') : t('auth:register.submit'))}
              </button>
            </form>

            {/* Divider only if OAuth methods are also enabled */}
            {hasOAuthMethods(loginConfig) && (
              <div style={dividerStyle}>
                <span style={dividerTextStyle}>{t('common:or')}</span>
              </div>
            )}
          </>
        )}

        {/* OAuth Login Buttons */}
        {loginConfig.enableGoogleLogin && (
          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-button"
            style={googleButtonStyle}
            disabled={isLoading}
          >
            <span style={googleIconStyle}>G</span>
            {t('common:continueWithGoogle')}
          </button>
        )}

        {loginConfig.enableMicrosoftLogin && (
          <button
            onClick={handleMicrosoftLogin}
            data-testid="microsoft-login-button"
            style={microsoftButtonStyle}
            disabled={isLoading}
          >
            <span style={microsoftIconStyle}>M</span>
            {t('common:continueWithMicrosoft')}
          </button>
        )}

        {/* Switch between Login/Register - only show if email login is enabled */}
        {loginConfig.enableEmailLogin && (
          <div style={switchModeStyle}>
            <span>
              {isLogin ? t('auth:login.noAccount') + ' ' : t('auth:register.hasAccount') + ' '}
            </span>
            <button
              type="button"
              onClick={toggleMode}
              style={switchButtonStyle}
              disabled={isLoading}
            >
              {isLogin ? t('auth:login.signUp') : t('auth:register.signIn')}
            </button>
          </div>
        )}

        <div style={backLinkStyle}>
          <Link to="/" style={backLinkTextStyle}>
            ← {t('common:backToHome')}
          </Link>
        </div>

        {/* Login Help Component - only show if demo credentials are enabled */}
        {loginConfig.showDemoCredentials && <LoginHelp className="mt-4" />}
      </div>
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  minHeight: 'calc(100vh - 80px)',
  backgroundColor: '#f8f9fa',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem 1rem',
};

const formContainerStyle: React.CSSProperties = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '8px',
  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  width: '100%',
  maxWidth: '400px',
};

const headerStyle: React.CSSProperties = {
  textAlign: 'center',
  marginBottom: '2rem',
};

const titleStyle: React.CSSProperties = {
  fontSize: '2rem',
  color: '#2c3e50',
  marginBottom: '0.5rem',
};

const subtitleStyle: React.CSSProperties = {
  color: '#7f8c8d',
  fontSize: '0.9rem',
};

const errorStyle: React.CSSProperties = {
  backgroundColor: '#fee',
  color: '#c33',
  padding: '1rem',
  borderRadius: '4px',
  marginBottom: '1rem',
  fontSize: '0.9rem',
  border: '1px solid #fcc',
};

const formStyle: React.CSSProperties = {
  marginBottom: '1.5rem',
};

const fieldGroupStyle: React.CSSProperties = {
  marginBottom: '1rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.5rem',
  color: '#2c3e50',
  fontSize: '0.9rem',
  fontWeight: 'bold',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '1rem',
  boxSizing: 'border-box',
};

const submitButtonStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: '#3498db',
  color: 'white',
  padding: '0.75rem',
  border: 'none',
  borderRadius: '4px',
  fontSize: '1rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const dividerStyle: React.CSSProperties = {
  textAlign: 'center',
  margin: '1.5rem 0',
  position: 'relative',
};

const dividerTextStyle: React.CSSProperties = {
  backgroundColor: 'white',
  padding: '0 1rem',
  color: '#7f8c8d',
  fontSize: '0.9rem',
};

const googleButtonStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: 'white',
  color: '#333',
  padding: '0.75rem',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '1rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  marginBottom: '1.5rem',
  transition: 'background-color 0.2s',
};

const googleIconStyle: React.CSSProperties = {
  backgroundColor: '#4285f4',
  color: 'white',
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.8rem',
  fontWeight: 'bold',
};

const microsoftButtonStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: 'white',
  color: '#333',
  padding: '0.75rem',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '1rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  marginBottom: '1.5rem',
  transition: 'background-color 0.2s',
};

const microsoftIconStyle: React.CSSProperties = {
  backgroundColor: '#00a1f1',
  color: 'white',
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.8rem',
  fontWeight: 'bold',
};

const switchModeStyle: React.CSSProperties = {
  textAlign: 'center',
  fontSize: '0.9rem',
  color: '#666',
  marginBottom: '2rem',
};

const switchButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#3498db',
  cursor: 'pointer',
  textDecoration: 'underline',
  fontSize: '0.9rem',
};



const backLinkStyle: React.CSSProperties = {
  textAlign: 'center',
};

const backLinkTextStyle: React.CSSProperties = {
  color: '#3498db',
  textDecoration: 'none',
  fontSize: '0.9rem',
};

export default Login;
